import { formatDisplayName } from '@/common/utils/formatDisplayName';
import type {
  AgentProfileFields,
  AgentTeamMembershipIdentifiers,
} from '$agent-profiles';
import type { CoreRequest, IdentityRequest } from '$api-clients/hooks';
import type {
  ContactSuggestionContact,
  ContactSuggestionResult,
} from './types';

export type RestTeamMember =
  IdentityRequest<'/team-members'>['Data']['data'][number];

export type GoogleContact =
  CoreRequest<'/user-settings/{id}/google-contact/search'>['Data'][number];

export type FollowUpBossContact =
  CoreRequest<'/users/{id}/integrations/follow-up-boss/contacts'>['Data']['results'][number];

export function mapTeamMemberToContactSuggestion(
  member: RestTeamMember,
): ContactSuggestionResult {
  const { team } = member;
  const supervisingBroker = team.supervisingBroker;

  return {
    userId: member.userId,
    googleId: null,
    followUpBossId: null,
    contactSource: null,
    email: member.contact.email,
    firstName: member.firstName,
    lastName: member.lastName,
    photoUrl: member.photoUrl ?? null,
    displayName: formatDisplayName({
      firstName: member.firstName,
      lastName: member.lastName,
      teamName: team.legalName ?? undefined,
      teamAlias: team.alias,
    }),
    contactResponse: {
      // `legacyExternalId` can come back as "" (not null), so use `||` to fall
      // back to the identity UUID rather than keep a blank id.
      userId: member.legacyExternalId || member.userId,
      teamId: team.legacyExternalId || team.id,
      userIdentityId: member.userId,
      teamIdentityId: team.id,
      roles: member.roles ?? null,
      licenseNumber: member.license?.number ?? null,
      licenseState: member.license?.state ?? null,
      state: member.license?.state ?? null,
      cellPhone: member.contact.phone ?? null,
      brokerage: { companyName: team.legalName ?? null },
      supervisingBroker: {
        firstName: supervisingBroker?.firstName ?? null,
        lastName: supervisingBroker?.lastName ?? null,
        licenseNumber: supervisingBroker?.license?.number ?? null,
        cellPhone: supervisingBroker?.contact?.phone ?? null,
        email: supervisingBroker?.contact?.email ?? null,
      },
    },
  };
}

/**
 * Field semantics mirror core-service's `toAgentCreateDto`: the brokerage
 * address is the team's registered address, and `companyName` carries the
 * referral-agent LFRO swap applied upstream in `mapToAgentProfile`.
 */
export function mapAgentProfileToContact(
  profile: AgentProfileFields,
): ContactSuggestionContact {
  return {
    userId: profile.id,
    userIdentityId: profile.userIdentityId,
    teamId: profile.teamId,
    teamIdentityId: profile.teamIdentityId,
    teamName: profile.teamName,
    companyName: profile.companyName,
    firstName: profile.firstName,
    lastName: profile.lastName,
    email: profile.email || null,
    cellPhone: profile.cellPhone,
    licenseNumber: profile.licenseNumber,
    licenseState: profile.licenseState,
    mlsId: profile.mlsId,
    brokerage: {
      companyName: profile.companyName,
      licenseNumber: profile.brokerageLicenseNumber,
      mlsCode: profile.brokerageMlsCode,
      officeEmail: profile.brokerageOfficeEmail,
      email: profile.brokerageEmail,
      phone: profile.brokerageCellPhone,
      cellPhone: profile.brokerageCellPhone,
      street1: profile.street1,
      street2: profile.street2,
      city: profile.city,
      state: profile.state,
      zip: profile.zipCode,
    },
    supervisingBroker: profile.supervisingBroker,
    broker: profile.broker,
    officeManager: profile.officeManager,
  };
}

function getUserDefinedField(
  contact: GoogleContact,
  fieldName: string,
): string | null {
  return (
    contact.userDefined?.find((field) => field?.key === fieldName)?.value ||
    null
  );
}

function mapGoogleContactToContact(
  contact: GoogleContact,
): ContactSuggestionContact {
  const address =
    contact.addresses?.find((option) => /home/.exec(option?.type || '')) ||
    contact.addresses?.[0] ||
    null;

  const state =
    address?.region
      ?.split('\n')
      ?.reduce((acc, line) => (/County/.exec(line ?? '') ? acc : line), '') ||
    null;

  const email =
    (
      contact.emailAddresses?.find((option) => option?.metadata?.primary) ||
      contact.emailAddresses?.[0]
    )?.value || null;

  const homePhone =
    contact.phoneNumbers?.reduce<string>(
      (acc, option) =>
        /home/.exec(option?.type || '') || option?.type === 'work'
          ? option.value || ''
          : acc,
      '',
    ) || null;

  const cellPhone =
    (
      contact.phoneNumbers?.find((option) =>
        /mobile/.exec(option.type || ''),
      ) || contact.phoneNumbers?.[0]
    )?.value || null;

  return {
    email,
    homePhone,
    cellPhone,
    companyName:
      contact.organizations?.[0]?.name ||
      getUserDefinedField(contact, 'Company Name'),
    licenseNumber:
      getUserDefinedField(contact, 'License Number') ||
      getUserDefinedField(contact, 'Agent License Number'),
    entityType: getUserDefinedField(contact, 'Entity Type') || 'individual',
    isSuccessor: getUserDefinedField(contact, 'Is Successor') === 'true',
    googleContactId: contact.resourceName?.split('/')?.pop() || null,
    city: address?.city || null,
    street1: address?.streetAddress?.split('\n')?.[0] || null,
    street2: address?.streetAddress?.split('\n')?.[1] || null,
    zip: address?.postalCode || null,
    state,
    brokerage: {
      companyName:
        getUserDefinedField(contact, 'Brokerage Company Name') ||
        getUserDefinedField(contact, 'Brokerage Company'),
      licenseNumber: getUserDefinedField(contact, 'Brokerage License Number'),
      street1:
        getUserDefinedField(contact, 'Brokerage Street 1') ||
        getUserDefinedField(contact, 'Brokerage Street'),
      street2:
        getUserDefinedField(contact, 'Brokerage Street 2') ||
        getUserDefinedField(contact, 'Brokerage Apt/Unit/Suite'),
      city: getUserDefinedField(contact, 'Brokerage City'),
      state: getUserDefinedField(contact, 'Brokerage State'),
      zip: getUserDefinedField(contact, 'Brokerage Zip'),
      cellPhone:
        getUserDefinedField(contact, 'Brokerage Cell Phone') ||
        getUserDefinedField(contact, 'Brokerage Phone'),
      email: getUserDefinedField(contact, 'Brokerage Email'),
      officeEmail: getUserDefinedField(contact, 'Brokerage Office Email'),
    },
  };
}

export function mapGoogleContactToContactSuggestion(
  contact: GoogleContact,
): ContactSuggestionResult {
  const [name] = contact.names ?? [];
  const firstName = name?.givenName || null;
  const lastName = name?.familyName || null;
  const mapped = mapGoogleContactToContact(contact);

  return {
    userId: null,
    googleId: mapped.googleContactId ?? null,
    followUpBossId: null,
    contactSource: contact.resourceName?.split('/')?.shift() || null,
    email: mapped.email ?? null,
    firstName,
    lastName,
    photoUrl:
      (
        contact.photos?.find((option) => option?.metadata?.primary) ||
        contact.photos?.[0]
      )?.url || null,
    displayName: formatDisplayName({ firstName, lastName }),
    contactResponse: mapped,
  };
}

export function mapFollowUpBossContactToContactSuggestion(
  contact: FollowUpBossContact,
): ContactSuggestionResult {
  const { contactResponse } = contact;

  return {
    userId: null,
    googleId: null,
    followUpBossId: contactResponse?.id ?? null,
    contactSource: contact.contactSource ?? null,
    email: contact.email ?? null,
    firstName: contact.firstName ?? null,
    lastName: contact.lastName ?? null,
    photoUrl: contact.photoUrl ?? null,
    displayName: contact.displayName,
    contactResponse: {
      email: contactResponse?.email ?? null,
      homePhone: contactResponse?.homePhone ?? null,
      cellPhone: contactResponse?.cellPhone ?? null,
      street1: contactResponse?.street1 ?? null,
      street2: contactResponse?.street2 ?? null,
      city: contactResponse?.city ?? null,
      state: contactResponse?.state ?? null,
      zip: contactResponse?.zip ?? null,
    },
  };
}

export function mapContactSuggestionToTeamMembershipIdentifiers(
  suggestion: ContactSuggestionResult,
): AgentTeamMembershipIdentifiers {
  const { contactResponse } = suggestion;

  return {
    id: contactResponse?.userId ?? '',
    teamId: contactResponse?.teamId ?? '',
    // The suggestion's top-level userId is the identity id in every producer,
    // so it backfills sparse contactResponses (e.g. the default suggestion).
    userIdentityId: contactResponse?.userIdentityId ?? suggestion.userId ?? '',
    teamIdentityId: contactResponse?.teamIdentityId ?? '',
  };
}
